import React, { useEffect, useState } from 'react';
import {
  Container,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Box,
  CircularProgress,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Tabs,
  Tab,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { Assignment as AssignmentIcon, Add as AddIcon, Edit as EditIcon, Visibility as VisibilityIcon, ExpandMore as ExpandMoreIcon, ExpandLess as ExpandLessIcon } from '@mui/icons-material';
import TextField from '@mui/material/TextField';
import Layout from '../../components/Layout';
import api from '../../services/api';

const ManagerJobs = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [jobs, setJobs] = useState([]);
  const [activeJobs, setActiveJobs] = useState([]);
  const [completedUnpaidJobs, setCompletedUnpaidJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tabValue, setTabValue] = useState(0);
  const [assignDialogOpen, setAssignDialogOpen] = useState(false);
  const [selectedJob, setSelectedJob] = useState(null);
  const [drivers, setDrivers] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [assignData, setAssignData] = useState({
    driver_id: '',
    vehicle_id: '',
  });
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [viewingJob, setViewingJob] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [createData, setCreateData] = useState({
    customer: '',
    delivery_address: '',
    delivery_city: '',
    delivery_state: 'WA',
    delivery_zip: '',
    delivery_notes: '',
    diesel_quantity: '',
    def_quantity: '',
    frequency: 'one-time',
    scheduled_date: '',
  });
  const [editData, setEditData] = useState({
    delivery_address: '',
    delivery_city: '',
    delivery_state: 'WA',
    delivery_zip: '',
    delivery_notes: '',
    diesel_quantity: '',
    def_quantity: '',
    frequency: 'one-time',
    scheduled_date: '',
    job_status: 'pending',
    payment_status: 'pending',
  });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [apiError, setApiError] = useState('');

  useEffect(() => {
    fetchJobs();
    fetchActiveJobs();
    fetchCompletedUnpaidJobs();
    fetchDrivers();
    fetchVehicles();
    fetchCustomers();
  }, []);

  const fetchJobs = async () => {
    try {
      setApiError('');
      const response = await api.get('/managers/jobs/');
      // Ensure response.data is an array
      const jobsData = Array.isArray(response.data) ? response.data : [];
      setJobs(jobsData);
    } catch (error) {
      console.error('Error fetching jobs:', error);
      setJobs([]);
      const errorMsg = error.response?.data?.detail || 
                      error.response?.data?.error || 
                      'Failed to load jobs';
      setApiError(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const fetchActiveJobs = async () => {
    try {
      const response = await api.get('/managers/jobs_active/');
      // Ensure response.data is an array
      const activeJobsData = Array.isArray(response.data) ? response.data : [];
      setActiveJobs(activeJobsData);
    } catch (error) {
      console.error('Error fetching active jobs:', error);
      setActiveJobs([]);
    }
  };

  const fetchCompletedUnpaidJobs = async () => {
    try {
      const response = await api.get('/managers/jobs/');
      // Filter for completed jobs that are not fully paid
      const allJobs = Array.isArray(response.data) ? response.data : [];
      const unpaidCompleted = allJobs.filter(job => 
        job?.job_status === 'complete' && 
        job?.payment_status !== 'paid'
      );
      setCompletedUnpaidJobs(unpaidCompleted);
    } catch (error) {
      console.error('Error fetching completed unpaid jobs:', error);
      setCompletedUnpaidJobs([]);
    }
  };

  const fetchDrivers = async () => {
    try {
      const response = await api.get('/managers/available_drivers/');
      // Ensure response.data is an array
      const driversData = Array.isArray(response.data) ? response.data : [];
      setDrivers(driversData);
    } catch (error) {
      console.error('Error fetching drivers:', error);
      setDrivers([]);
    }
  };

  const fetchVehicles = async () => {
    try {
      const response = await api.get('/vehicles/');
      // Ensure response.data is an array
      const vehiclesData = Array.isArray(response.data) ? response.data : [];
      setVehicles(vehiclesData);
    } catch (error) {
      console.error('Error fetching vehicles:', error);
      setVehicles([]);
    }
  };

  const fetchCustomers = async () => {
    try {
      const response = await api.get('/managers/customers/');
      const customersData = Array.isArray(response.data) ? response.data : [];
      setCustomers(customersData);
    } catch (error) {
      console.error('Error fetching customers:', error);
      setCustomers([]);
    }
  };

  const handleOpenAssignDialog = (job) => {
    setSelectedJob(job);
    setAssignDialogOpen(true);
    setError('');
    setSuccess('');
    setAssignData({
      driver_id: '',
      vehicle_id: '',
    });
  };

  const handleCloseAssignDialog = () => {
    setAssignDialogOpen(false);
    setSelectedJob(null);
    setError('');
    setSuccess('');
  };

  const handleAssignChange = (e) => {
    setAssignData({
      ...assignData,
      [e.target.name]: e.target.value,
    });
  };

  const handleOpenCreateDialog = () => {
    setCreateDialogOpen(true);
    setError('');
    setSuccess('');
    setCreateData({
      customer: '',
      delivery_address: '',
      delivery_city: '',
      delivery_state: 'WA',
      delivery_zip: '',
      delivery_notes: '',
      diesel_quantity: '',
      def_quantity: '',
      frequency: 'one-time',
      scheduled_date: '',
    });
  };

  const handleCloseCreateDialog = () => {
    setCreateDialogOpen(false);
    setError('');
    setSuccess('');
  };

  const handleCreateChange = (e) => {
    setCreateData({
      ...createData,
      [e.target.name]: e.target.value,
    });
  };

  const handleOpenEditDialog = (job) => {
    setSelectedJob(job);
    setEditDialogOpen(true);
    setError('');
    setSuccess('');
    setEditData({
      delivery_address: job?.delivery_address || '',
      delivery_city: job?.delivery_city || '',
      delivery_state: job?.delivery_state || '',
      delivery_zip: job?.delivery_zip || '',
      delivery_notes: job?.delivery_notes || '',
      diesel_quantity: job?.diesel_quantity || '',
      def_quantity: job?.def_quantity || '',
      frequency: job?.frequency || 'one-time',
      scheduled_date: job?.scheduled_date ? new Date(job.scheduled_date).toISOString().slice(0, 16) : '',
      job_status: job?.job_status || 'pending',
      payment_status: job?.payment_status || 'pending',
    });
  };

  const handleCloseEditDialog = () => {
    setEditDialogOpen(false);
    setSelectedJob(null);
    setError('');
    setSuccess('');
  };

  const handleEditChange = (e) => {
    setEditData({
      ...editData,
      [e.target.name]: e.target.value,
    });
  };

  const handleUpdateJob = async () => {
    setError('');
    setSuccess('');

    if (!selectedJob?.id) {
      setError('Invalid job selected');
      return;
    }

    if (!editData.delivery_address || !editData.delivery_city || !editData.delivery_state || !editData.delivery_zip) {
      setError('All delivery location fields are required (Address, City, State, ZIP)');
      return;
    }

    try {
      const payload = {
        delivery_address: editData.delivery_address.trim(),
        delivery_city: editData.delivery_city.trim(),
        delivery_state: editData.delivery_state.trim(),
        delivery_zip: editData.delivery_zip.trim(),
        delivery_notes: editData.delivery_notes || '',
        diesel_quantity: parseFloat(editData.diesel_quantity) || 0,
        def_quantity: parseFloat(editData.def_quantity) || 0,
        frequency: editData.frequency,
        job_status: editData.job_status,
        payment_status: editData.payment_status,
      };

      if (editData.scheduled_date) {
        payload.scheduled_date = editData.scheduled_date;
      }

      await api.patch(`/jobs/${selectedJob.id}/`, payload);
      setSuccess('Job updated successfully');
      fetchJobs();
      fetchActiveJobs();
      fetchCompletedUnpaidJobs();
      setTimeout(() => {
        handleCloseEditDialog();
      }, 1500);
    } catch (error) {
      console.error('Error updating job:', error);
      const errorMessage = error.response?.data?.error || 
                          error.response?.data?.detail || 
                          'Failed to update job';
      setError(errorMessage);
    }
  };

  const handleCreateJob = async () => {
    setError('');
    setSuccess('');

    if (!createData.customer || !createData.delivery_address || !createData.delivery_city || !createData.delivery_state || !createData.delivery_zip) {
      setError('Please fill in all required fields (Customer and all Delivery Location fields)');
      return;
    }

    try {
      const payload = {
        customer: createData.customer,
        delivery_address: createData.delivery_address,
        delivery_city: createData.delivery_city,
        delivery_state: createData.delivery_state,
        delivery_zip: createData.delivery_zip,
        diesel_quantity: parseFloat(createData.diesel_quantity) || 0,
        def_quantity: parseFloat(createData.def_quantity) || 0,
        frequency: createData.frequency,
      };

      if (createData.scheduled_date) {
        payload.scheduled_date = createData.scheduled_date;
      }

      await api.post('/jobs/', payload);
      setSuccess('Job created successfully');
      fetchJobs();
      fetchActiveJobs();
      fetchCompletedUnpaidJobs();
      setTimeout(() => {
        handleCloseCreateDialog();
      }, 1500);
    } catch (error) {
      console.error('Error creating job:', error);
      const errorMessage = error.response?.data?.error || 
                          error.response?.data?.detail || 
                          'Failed to create job';
      setError(errorMessage);
    }
  };

  const handleAssign = async () => {
    setError('');
    setSuccess('');

    if (!assignData.driver_id) {
      setError('Please select a driver');
      return;
    }

    try {
      if (!selectedJob?.id) {
        setError('Invalid job selected');
        return;
      }
      await api.post('/managers/assign_job/', {
        job_id: selectedJob.id,
        driver_id: assignData.driver_id,
        vehicle_id: assignData.vehicle_id || null,
      });
      setSuccess('Job assigned successfully');
      fetchJobs();
      fetchActiveJobs();
      fetchCompletedUnpaidJobs();
      setTimeout(() => {
        handleCloseAssignDialog();
      }, 1500);
    } catch (error) {
      setError(error.response?.data?.error || 'Failed to assign job');
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: 'warning',
      accepted: 'info',
      active: 'primary',
      complete: 'success',
      cancelled: 'error',
    };
    return colors[status] || 'default';
  };

  if (loading) {
    return (
      <Layout>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
          <CircularProgress />
        </Box>
      </Layout>
    );
  }

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const getDisplayJobs = () => {
    if (tabValue === 0) {
      return activeJobs || [];
    } else if (tabValue === 1) {
      return jobs || [];
    } else if (tabValue === 2) {
      return completedUnpaidJobs || [];
    }
    return [];
  };

  const displayJobs = getDisplayJobs();

  return (
    <Layout>
      <Container maxWidth={false} sx={{ px: { xs: 1, sm: 2, md: 3 } }}>
        <Box 
          display="flex" 
          justifyContent="space-between" 
          alignItems="center" 
          mb={2}
          sx={{ flexDirection: { xs: 'column', sm: 'row' }, gap: { xs: 2, sm: 0 } }}
        >
          <Typography variant="h4">
            Jobs
          </Typography>
          <Button variant="contained" startIcon={<AddIcon />} onClick={handleOpenCreateDialog}>
            Create Job
          </Button>
        </Box>
        <Tabs value={tabValue} onChange={handleTabChange} sx={{ mb: 2 }}>
          <Tab label="Active Jobs" />
          <Tab label="All Jobs" />
          <Tab label={`Completed (Not Paid) (${completedUnpaidJobs.length})`} />
        </Tabs>
        {apiError && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setApiError('')}>
            {apiError}
          </Alert>
        )}
        {success && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
            {success}
          </Alert>
        )}
        <TableContainer 
          component={Paper} 
          sx={{ 
            mt: 3,
            overflowX: 'auto',
            maxWidth: '100%',
          }}
        >
          <Table sx={{ minWidth: 650 }}>
            <TableHead>
              <TableRow>
                <TableCell>Job ID</TableCell>
                <TableCell>Customer</TableCell>
                <TableCell>Location</TableCell>
                <TableCell>Quantity</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Payment Status</TableCell>
                <TableCell>Driver</TableCell>
                <TableCell>Vehicle</TableCell>
                <TableCell>Created</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {displayJobs.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={10} align="center">
                    <Typography variant="body2" color="textSecondary">
                      No jobs found
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                displayJobs.map((job) => (
                  <TableRow key={job?.id || Math.random()}>
                    <TableCell>{job?.id ? job.id.substring(0, 8) : 'N/A'}</TableCell>
                    <TableCell>{job?.customer_name || 'Unknown'}</TableCell>
                    <TableCell>
                      {job?.delivery_address 
                        ? `${job.delivery_address}${job.delivery_city ? `, ${job.delivery_city}` : ''}${job.delivery_state ? `, ${job.delivery_state}` : ''}${job.delivery_zip ? ` ${job.delivery_zip}` : ''}`
                        : (job?.job_location || 'N/A')}
                    </TableCell>
                    <TableCell>
                      <Box>
                        <Box>
                          {job?.diesel_quantity > 0 && `${job.diesel_quantity}L Diesel`}
                          {job?.diesel_quantity > 0 && job?.def_quantity > 0 && ' / '}
                          {job?.def_quantity > 0 && `${job.def_quantity}L DEF`}
                          {(!job?.diesel_quantity || job.diesel_quantity === 0) && (!job?.def_quantity || job.def_quantity === 0) && 'N/A'}
                        </Box>
                        {(job?.delivered_diesel > 0 || job?.delivered_def > 0) && (
                          <Box sx={{ mt: 0.5, fontSize: '0.75rem', color: 'text.secondary' }}>
                            Delivered: {' '}
                            {job?.delivered_diesel > 0 && `${job.delivered_diesel}L Diesel`}
                            {job?.delivered_diesel > 0 && job?.delivered_def > 0 && ' / '}
                            {job?.delivered_def > 0 && `${job.delivered_def}L DEF`}
                          </Box>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={job?.job_status || 'unknown'}
                        color={getStatusColor(job?.job_status)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={job?.payment_status || 'unknown'}
                        color={
                          job?.payment_status === 'paid' ? 'success' :
                          job?.payment_status === 'partial' ? 'warning' :
                          job?.payment_status === 'overdue' ? 'error' :
                          job?.payment_status === 'pending' ? 'info' :
                          'default'
                        }
                        size="small"
                      />
                    </TableCell>
                    <TableCell>{job?.driver_name || 'Not assigned'}</TableCell>
                    <TableCell>{job?.vehicle_info || 'Not assigned'}</TableCell>
                    <TableCell>
                      {job?.created_at 
                        ? new Date(job.created_at).toLocaleDateString() 
                        : 'N/A'}
                    </TableCell>
                    <TableCell>
                      {job?.job_status === 'complete' && (
                        <Button
                          variant="outlined"
                          size="small"
                          startIcon={<VisibilityIcon />}
                          onClick={async () => {
                            // Fetch full job details including delivery_completion and sub_jobs
                            try {
                              const response = await api.get(`/jobs/${job.id}/`);
                              setViewingJob(response.data);
                              setViewDialogOpen(true);
                            } catch (error) {
                              console.error('Error fetching job details:', error);
                              // Fallback to using the job from the list
                              setViewingJob(job);
                              setViewDialogOpen(true);
                            }
                          }}
                          sx={{ mr: 1 }}
                          color="info"
                        >
                          View Details
                        </Button>
                      )}
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={<EditIcon />}
                        onClick={() => handleOpenEditDialog(job)}
                        sx={{ mr: 1 }}
                      >
                        Edit
                      </Button>
                      {(!job?.driver || job?.job_status === 'pending') && (
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={<AssignmentIcon />}
                          onClick={() => handleOpenAssignDialog(job)}
                        >
                          Assign
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <Dialog open={assignDialogOpen} onClose={handleCloseAssignDialog} maxWidth="sm" fullWidth>
          <DialogTitle>Assign Job to Driver</DialogTitle>
          <DialogContent>
            {error && (
              <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
                {error}
              </Alert>
            )}
            {selectedJob && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="textSecondary">
                  Job: {selectedJob?.delivery_address 
                    ? `${selectedJob.delivery_address}${selectedJob.delivery_city ? `, ${selectedJob.delivery_city}` : ''}${selectedJob.delivery_state ? `, ${selectedJob.delivery_state}` : ''}${selectedJob.delivery_zip ? ` ${selectedJob.delivery_zip}` : ''}`
                    : (selectedJob?.job_location || 'N/A')} - 
                  {selectedJob?.diesel_quantity > 0 && ` ${selectedJob.diesel_quantity}L Diesel`}
                  {selectedJob?.diesel_quantity > 0 && selectedJob?.def_quantity > 0 && ' /'}
                  {selectedJob?.def_quantity > 0 && ` ${selectedJob.def_quantity}L DEF`}
                  {(!selectedJob?.diesel_quantity || selectedJob.diesel_quantity === 0) && (!selectedJob?.def_quantity || selectedJob.def_quantity === 0) && ' 0L'}
                </Typography>
                {(selectedJob?.delivered_diesel > 0 || selectedJob?.delivered_def > 0) && (
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    <strong>Delivered:</strong> {' '}
                    {selectedJob?.delivered_diesel > 0 && `${selectedJob.delivered_diesel}L Diesel`}
                    {selectedJob?.delivered_diesel > 0 && selectedJob?.delivered_def > 0 && ' / '}
                    {selectedJob?.delivered_def > 0 && `${selectedJob.delivered_def}L DEF`}
                  </Typography>
                )}
                <Typography variant="body2" color="textSecondary">
                  Customer: {selectedJob?.customer_name || 'Unknown'}
                </Typography>
              </Box>
            )}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
              <FormControl fullWidth required>
                <InputLabel>Driver</InputLabel>
                <Select
                  name="driver_id"
                  value={assignData.driver_id}
                  onChange={handleAssignChange}
                  label="Driver"
                >
                  {!Array.isArray(drivers) || drivers.length === 0 ? (
                    <MenuItem disabled>No available drivers</MenuItem>
                  ) : (
                    drivers.map((driver) => (
                      <MenuItem key={driver?.id || Math.random()} value={driver?.id}>
                        {driver?.name || 'Unknown'} ({driver?.status || 'unknown'})
                        {driver?.current_vehicle_info?.license_plate && 
                          ` - ${driver.current_vehicle_info.license_plate}`}
                      </MenuItem>
                    ))
                  )}
                </Select>
              </FormControl>

              <FormControl fullWidth>
                <InputLabel>Vehicle (Optional)</InputLabel>
                <Select
                  name="vehicle_id"
                  value={assignData.vehicle_id}
                  onChange={handleAssignChange}
                  label="Vehicle (Optional)"
                >
                  <MenuItem value="">None</MenuItem>
                  {!Array.isArray(vehicles) || vehicles.length === 0 ? (
                    <MenuItem disabled>No vehicles available</MenuItem>
                  ) : (
                    vehicles.map((vehicle) => (
                      <MenuItem key={vehicle?.id || Math.random()} value={vehicle?.id}>
                        {vehicle?.license_plate || 'N/A'} - {vehicle?.make || ''} {vehicle?.model || ''}
                        {vehicle?.driver_name && ` (${vehicle.driver_name})`}
                      </MenuItem>
                    ))
                  )}
                </Select>
              </FormControl>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseAssignDialog}>Cancel</Button>
            <Button onClick={handleAssign} variant="contained">
              Assign
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog open={createDialogOpen} onClose={handleCloseCreateDialog} maxWidth="md" fullWidth>
          <DialogTitle>Create Job for Customer</DialogTitle>
          <DialogContent>
            {error && (
              <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
                {error}
              </Alert>
            )}
            {success && (
              <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
                {success}
              </Alert>
            )}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
              <FormControl fullWidth required>
                <InputLabel>Customer</InputLabel>
                <Select
                  name="customer"
                  value={createData.customer}
                  onChange={handleCreateChange}
                  label="Customer"
                >
                  {!Array.isArray(customers) || customers.length === 0 ? (
                    <MenuItem disabled>No customers available</MenuItem>
                  ) : (
                    customers.map((customer) => (
                      <MenuItem key={customer?.id || Math.random()} value={customer?.id}>
                        {customer?.company_name || customer?.name || 'Unknown'} ({customer?.email || 'N/A'})
                      </MenuItem>
                    ))
                  )}
                </Select>
              </FormControl>

              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600, color: '#1E3A5F' }}>
                Delivery Location
              </Typography>
              <TextField
                name="delivery_address"
                label="Street Address"
                value={createData.delivery_address}
                onChange={handleCreateChange}
                required
                fullWidth
              />
              <Box sx={{ display: 'flex', gap: 2 }}>
                <TextField
                  name="delivery_city"
                  label="City"
                  value={createData.delivery_city}
                  onChange={handleCreateChange}
                  required
                  fullWidth
                />
                <FormControl required sx={{ width: '30%' }}>
                  <InputLabel>State</InputLabel>
                  <Select
                    name="delivery_state"
                    value={createData.delivery_state}
                    onChange={handleCreateChange}
                    label="State"
                  >
                    <MenuItem value="AL">AL - Alabama</MenuItem>
                    <MenuItem value="AK">AK - Alaska</MenuItem>
                    <MenuItem value="AZ">AZ - Arizona</MenuItem>
                    <MenuItem value="AR">AR - Arkansas</MenuItem>
                    <MenuItem value="CA">CA - California</MenuItem>
                    <MenuItem value="CO">CO - Colorado</MenuItem>
                    <MenuItem value="CT">CT - Connecticut</MenuItem>
                    <MenuItem value="DE">DE - Delaware</MenuItem>
                    <MenuItem value="FL">FL - Florida</MenuItem>
                    <MenuItem value="GA">GA - Georgia</MenuItem>
                    <MenuItem value="HI">HI - Hawaii</MenuItem>
                    <MenuItem value="ID">ID - Idaho</MenuItem>
                    <MenuItem value="IL">IL - Illinois</MenuItem>
                    <MenuItem value="IN">IN - Indiana</MenuItem>
                    <MenuItem value="IA">IA - Iowa</MenuItem>
                    <MenuItem value="KS">KS - Kansas</MenuItem>
                    <MenuItem value="KY">KY - Kentucky</MenuItem>
                    <MenuItem value="LA">LA - Louisiana</MenuItem>
                    <MenuItem value="ME">ME - Maine</MenuItem>
                    <MenuItem value="MD">MD - Maryland</MenuItem>
                    <MenuItem value="MA">MA - Massachusetts</MenuItem>
                    <MenuItem value="MI">MI - Michigan</MenuItem>
                    <MenuItem value="MN">MN - Minnesota</MenuItem>
                    <MenuItem value="MS">MS - Mississippi</MenuItem>
                    <MenuItem value="MO">MO - Missouri</MenuItem>
                    <MenuItem value="MT">MT - Montana</MenuItem>
                    <MenuItem value="NE">NE - Nebraska</MenuItem>
                    <MenuItem value="NV">NV - Nevada</MenuItem>
                    <MenuItem value="NH">NH - New Hampshire</MenuItem>
                    <MenuItem value="NJ">NJ - New Jersey</MenuItem>
                    <MenuItem value="NM">NM - New Mexico</MenuItem>
                    <MenuItem value="NY">NY - New York</MenuItem>
                    <MenuItem value="NC">NC - North Carolina</MenuItem>
                    <MenuItem value="ND">ND - North Dakota</MenuItem>
                    <MenuItem value="OH">OH - Ohio</MenuItem>
                    <MenuItem value="OK">OK - Oklahoma</MenuItem>
                    <MenuItem value="OR">OR - Oregon</MenuItem>
                    <MenuItem value="PA">PA - Pennsylvania</MenuItem>
                    <MenuItem value="RI">RI - Rhode Island</MenuItem>
                    <MenuItem value="SC">SC - South Carolina</MenuItem>
                    <MenuItem value="SD">SD - South Dakota</MenuItem>
                    <MenuItem value="TN">TN - Tennessee</MenuItem>
                    <MenuItem value="TX">TX - Texas</MenuItem>
                    <MenuItem value="UT">UT - Utah</MenuItem>
                    <MenuItem value="VT">VT - Vermont</MenuItem>
                    <MenuItem value="VA">VA - Virginia</MenuItem>
                    <MenuItem value="WA">WA - Washington</MenuItem>
                    <MenuItem value="WV">WV - West Virginia</MenuItem>
                    <MenuItem value="WI">WI - Wisconsin</MenuItem>
                    <MenuItem value="WY">WY - Wyoming</MenuItem>
                  </Select>
                </FormControl>
                <TextField
                  name="delivery_zip"
                  label="ZIP Code"
                  value={createData.delivery_zip}
                  onChange={handleCreateChange}
                  required
                  fullWidth
                  sx={{ width: '30%' }}
                />
              </Box>

              <TextField
                name="delivery_notes"
                label="Delivery Notes"
                value={createData.delivery_notes}
                onChange={handleCreateChange}
                fullWidth
                multiline
                rows={3}
                placeholder="Enter delivery notes and special instructions"
              />

              <Box sx={{ display: 'flex', gap: 2 }}>
                <TextField
                  name="diesel_quantity"
                  label="Diesel Quantity (Liters)"
                  type="number"
                  value={createData.diesel_quantity}
                  onChange={handleCreateChange}
                  fullWidth
                  inputProps={{ step: '0.01', min: '0' }}
                />

                <TextField
                  name="def_quantity"
                  label="DEF Quantity (Liters)"
                  type="number"
                  value={createData.def_quantity}
                  onChange={handleCreateChange}
                  fullWidth
                  inputProps={{ step: '0.01', min: '0' }}
                />
              </Box>

              <FormControl fullWidth>
                <InputLabel>Frequency</InputLabel>
                <Select
                  name="frequency"
                  value={createData.frequency}
                  onChange={handleCreateChange}
                  label="Frequency"
                >
                  <MenuItem value="one-time">One-time</MenuItem>
                  <MenuItem value="weekly">Weekly</MenuItem>
                  <MenuItem value="bi-weekly">Bi-weekly</MenuItem>
                  <MenuItem value="monthly">Monthly</MenuItem>
                </Select>
              </FormControl>

              <TextField
                name="scheduled_date"
                label="Scheduled Date (Optional)"
                type="datetime-local"
                value={createData.scheduled_date}
                onChange={handleCreateChange}
                fullWidth
                InputLabelProps={{ shrink: true }}
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseCreateDialog}>Cancel</Button>
            <Button onClick={handleCreateJob} variant="contained">
              Create Job
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog 
          open={editDialogOpen} 
          onClose={handleCloseEditDialog} 
          maxWidth="md" 
          fullWidth
          fullScreen={isMobile}
          sx={{
            '& .MuiDialog-paper': {
              m: { xs: 0, sm: 2 },
            },
          }}
        >
          <DialogTitle>Edit Job</DialogTitle>
          <DialogContent>
            {error && (
              <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
                {error}
              </Alert>
            )}
            {success && (
              <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
                {success}
              </Alert>
            )}
            {selectedJob && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="textSecondary">
                  Job ID: {selectedJob?.id ? selectedJob.id.substring(0, 8) : 'N/A'}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Customer: {selectedJob?.customer_name || 'Unknown'}
                </Typography>
              </Box>
            )}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600, color: '#1E3A5F' }}>
                Delivery Location
              </Typography>
              <TextField
                name="delivery_address"
                label="Street Address"
                value={editData.delivery_address}
                onChange={handleEditChange}
                required
                fullWidth
              />
              <Box sx={{ display: 'flex', gap: 2 }}>
                <TextField
                  name="delivery_city"
                  label="City"
                  value={editData.delivery_city}
                  onChange={handleEditChange}
                  required
                  fullWidth
                />
                <FormControl required sx={{ width: '30%' }}>
                  <InputLabel>State</InputLabel>
                  <Select
                    name="delivery_state"
                    value={editData.delivery_state}
                    onChange={handleEditChange}
                    label="State"
                  >
                    <MenuItem value="AL">AL - Alabama</MenuItem>
                    <MenuItem value="AK">AK - Alaska</MenuItem>
                    <MenuItem value="AZ">AZ - Arizona</MenuItem>
                    <MenuItem value="AR">AR - Arkansas</MenuItem>
                    <MenuItem value="CA">CA - California</MenuItem>
                    <MenuItem value="CO">CO - Colorado</MenuItem>
                    <MenuItem value="CT">CT - Connecticut</MenuItem>
                    <MenuItem value="DE">DE - Delaware</MenuItem>
                    <MenuItem value="FL">FL - Florida</MenuItem>
                    <MenuItem value="GA">GA - Georgia</MenuItem>
                    <MenuItem value="HI">HI - Hawaii</MenuItem>
                    <MenuItem value="ID">ID - Idaho</MenuItem>
                    <MenuItem value="IL">IL - Illinois</MenuItem>
                    <MenuItem value="IN">IN - Indiana</MenuItem>
                    <MenuItem value="IA">IA - Iowa</MenuItem>
                    <MenuItem value="KS">KS - Kansas</MenuItem>
                    <MenuItem value="KY">KY - Kentucky</MenuItem>
                    <MenuItem value="LA">LA - Louisiana</MenuItem>
                    <MenuItem value="ME">ME - Maine</MenuItem>
                    <MenuItem value="MD">MD - Maryland</MenuItem>
                    <MenuItem value="MA">MA - Massachusetts</MenuItem>
                    <MenuItem value="MI">MI - Michigan</MenuItem>
                    <MenuItem value="MN">MN - Minnesota</MenuItem>
                    <MenuItem value="MS">MS - Mississippi</MenuItem>
                    <MenuItem value="MO">MO - Missouri</MenuItem>
                    <MenuItem value="MT">MT - Montana</MenuItem>
                    <MenuItem value="NE">NE - Nebraska</MenuItem>
                    <MenuItem value="NV">NV - Nevada</MenuItem>
                    <MenuItem value="NH">NH - New Hampshire</MenuItem>
                    <MenuItem value="NJ">NJ - New Jersey</MenuItem>
                    <MenuItem value="NM">NM - New Mexico</MenuItem>
                    <MenuItem value="NY">NY - New York</MenuItem>
                    <MenuItem value="NC">NC - North Carolina</MenuItem>
                    <MenuItem value="ND">ND - North Dakota</MenuItem>
                    <MenuItem value="OH">OH - Ohio</MenuItem>
                    <MenuItem value="OK">OK - Oklahoma</MenuItem>
                    <MenuItem value="OR">OR - Oregon</MenuItem>
                    <MenuItem value="PA">PA - Pennsylvania</MenuItem>
                    <MenuItem value="RI">RI - Rhode Island</MenuItem>
                    <MenuItem value="SC">SC - South Carolina</MenuItem>
                    <MenuItem value="SD">SD - South Dakota</MenuItem>
                    <MenuItem value="TN">TN - Tennessee</MenuItem>
                    <MenuItem value="TX">TX - Texas</MenuItem>
                    <MenuItem value="UT">UT - Utah</MenuItem>
                    <MenuItem value="VT">VT - Vermont</MenuItem>
                    <MenuItem value="VA">VA - Virginia</MenuItem>
                    <MenuItem value="WA">WA - Washington</MenuItem>
                    <MenuItem value="WV">WV - West Virginia</MenuItem>
                    <MenuItem value="WI">WI - Wisconsin</MenuItem>
                    <MenuItem value="WY">WY - Wyoming</MenuItem>
                  </Select>
                </FormControl>
                <TextField
                  name="delivery_zip"
                  label="ZIP Code"
                  value={editData.delivery_zip}
                  onChange={handleEditChange}
                  required
                  fullWidth
                  sx={{ width: '30%' }}
                />
              </Box>

              <TextField
                name="delivery_notes"
                label="Delivery Notes"
                value={editData.delivery_notes}
                onChange={handleEditChange}
                fullWidth
                multiline
                rows={3}
                placeholder="Enter delivery notes and special instructions"
              />

              <Box sx={{ display: 'flex', gap: 2 }}>
                <TextField
                  name="diesel_quantity"
                  label="Diesel Quantity (Liters)"
                  type="number"
                  value={editData.diesel_quantity}
                  onChange={handleEditChange}
                  fullWidth
                  inputProps={{ step: '0.01', min: '0' }}
                />

                <TextField
                  name="def_quantity"
                  label="DEF Quantity (Liters)"
                  type="number"
                  value={editData.def_quantity}
                  onChange={handleEditChange}
                  fullWidth
                  inputProps={{ step: '0.01', min: '0' }}
                />
              </Box>

              <Box sx={{ display: 'flex', gap: 2 }}>
                <FormControl fullWidth>
                  <InputLabel>Job Status</InputLabel>
                  <Select
                    name="job_status"
                    value={editData.job_status}
                    onChange={handleEditChange}
                    label="Job Status"
                  >
                    <MenuItem value="pending">Pending</MenuItem>
                    <MenuItem value="accepted">Accepted</MenuItem>
                    <MenuItem value="active">Active</MenuItem>
                    <MenuItem value="complete">Complete</MenuItem>
                    <MenuItem value="cancelled">Cancelled</MenuItem>
                  </Select>
                </FormControl>

                <FormControl fullWidth>
                  <InputLabel>Payment Status</InputLabel>
                  <Select
                    name="payment_status"
                    value={editData.payment_status}
                    onChange={handleEditChange}
                    label="Payment Status"
                  >
                    <MenuItem value="not_submitted">Not Submitted</MenuItem>
                    <MenuItem value="pending">Pending</MenuItem>
                    <MenuItem value="paid">Paid</MenuItem>
                    <MenuItem value="partial">Partial</MenuItem>
                    <MenuItem value="overdue">Overdue</MenuItem>
                  </Select>
                </FormControl>
              </Box>

              <FormControl fullWidth>
                <InputLabel>Frequency</InputLabel>
                <Select
                  name="frequency"
                  value={editData.frequency}
                  onChange={handleEditChange}
                  label="Frequency"
                >
                  <MenuItem value="one-time">One-time</MenuItem>
                  <MenuItem value="weekly">Weekly</MenuItem>
                  <MenuItem value="bi-weekly">Bi-weekly</MenuItem>
                  <MenuItem value="monthly">Monthly</MenuItem>
                </Select>
              </FormControl>

              <TextField
                name="scheduled_date"
                label="Scheduled Date (Optional)"
                type="datetime-local"
                value={editData.scheduled_date}
                onChange={handleEditChange}
                fullWidth
                InputLabelProps={{ shrink: true }}
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseEditDialog}>Cancel</Button>
            <Button onClick={handleUpdateJob} variant="contained">
              Update Job
            </Button>
          </DialogActions>
        </Dialog>

        {/* View Job Details Dialog - Shows sub-jobs for completed jobs */}
        <Dialog 
          open={viewDialogOpen} 
          onClose={() => {
            setViewDialogOpen(false);
            setViewingJob(null);
          }} 
          maxWidth="md" 
          fullWidth
          fullScreen={isMobile}
        >
          <DialogTitle>
            Job Details {viewingJob?.id ? `(${viewingJob.id.substring(0, 8)})` : ''}
          </DialogTitle>
          <DialogContent>
            {viewingJob && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="h6" gutterBottom>
                  Job Information
                </Typography>
                <Box sx={{ mb: 3 }}>
                  <Typography variant="body2"><strong>Customer:</strong> {viewingJob.customer_name || 'N/A'}</Typography>
                  <Typography variant="body2"><strong>Location:</strong> {
                    viewingJob.delivery_address 
                      ? `${viewingJob.delivery_address}${viewingJob.delivery_city ? `, ${viewingJob.delivery_city}` : ''}${viewingJob.delivery_state ? `, ${viewingJob.delivery_state}` : ''}${viewingJob.delivery_zip ? ` ${viewingJob.delivery_zip}` : ''}`
                      : (viewingJob.job_location || 'N/A')
                  }</Typography>
                  <Typography variant="body2"><strong>Requested Quantities:</strong></Typography>
                  <Typography variant="body2" sx={{ ml: 2 }}>
                    <strong>Diesel:</strong> {viewingJob.diesel_quantity || 0}L
                  </Typography>
                  <Typography variant="body2" sx={{ ml: 2 }}>
                    <strong>DEF:</strong> {viewingJob.def_quantity || 0}L
                  </Typography>
                  {(viewingJob.delivered_diesel > 0 || viewingJob.delivered_def > 0) && (
                    <>
                      <Typography variant="body2" sx={{ mt: 1 }}><strong>Delivered Quantities:</strong></Typography>
                      <Typography variant="body2" sx={{ ml: 2 }}>
                        <strong>Diesel:</strong> {viewingJob.delivered_diesel || 0}L
                      </Typography>
                      <Typography variant="body2" sx={{ ml: 2 }}>
                        <strong>DEF:</strong> {viewingJob.delivered_def || 0}L
                      </Typography>
                    </>
                  )}
                  {viewingJob.delivery_notes && (
                    <Box sx={{ mt: 2, p: 1.5, bgcolor: 'background.default', borderRadius: 1 }}>
                      <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>Delivery Notes:</Typography>
                      <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                        {viewingJob.delivery_notes}
                      </Typography>
                    </Box>
                  )}
                  <Typography variant="body2" sx={{ mt: 2 }}><strong>Status:</strong> {viewingJob.job_status || 'N/A'}</Typography>
                  <Typography variant="body2"><strong>Payment Status:</strong> {viewingJob.payment_status || 'N/A'}</Typography>
                  {viewingJob.driver_name && (
                    <Typography variant="body2"><strong>Driver:</strong> {viewingJob.driver_name}</Typography>
                  )}
                  {viewingJob.vehicle_info && (
                    <Typography variant="body2"><strong>Vehicle:</strong> {viewingJob.vehicle_info}</Typography>
                  )}
                  {viewingJob.completed_at && (
                    <Typography variant="body2"><strong>Completed At:</strong> {new Date(viewingJob.completed_at).toLocaleString()}</Typography>
                  )}
                </Box>

                {/* Delivery Completion Information - Split by Sub-Jobs */}
                {viewingJob.delivery_completion && (
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="h6" gutterBottom>
                      Delivery Completion
                    </Typography>
                    
                    {/* Main Job Delivery Section */}
                    <Box sx={{ mb: 3 }}>
                      <Typography variant="h6" gutterBottom sx={{ color: 'primary.main', fontWeight: 600 }}>
                        Main Job Delivery
                      </Typography>
                      <Box sx={{ p: 2, bgcolor: 'primary.light', borderRadius: 1, border: '1px solid', borderColor: 'primary.main' }}>
                        <Typography variant="body2" sx={{ mb: 1 }}>
                          <strong>Delivery Address:</strong> {
                            viewingJob.delivery_address 
                              ? `${viewingJob.delivery_address}${viewingJob.delivery_city ? `, ${viewingJob.delivery_city}` : ''}${viewingJob.delivery_state ? `, ${viewingJob.delivery_state}` : ''}${viewingJob.delivery_zip ? ` ${viewingJob.delivery_zip}` : ''}`
                              : (viewingJob.job_location || 'N/A')
                          }
                        </Typography>
                        {/* Requested Quantities */}
                        {(viewingJob.diesel_quantity || viewingJob.def_quantity) && (
                          <Box sx={{ mb: 1.5, mt: 1.5 }}>
                            <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>Requested Quantities:</Typography>
                            {viewingJob.diesel_quantity && (
                              <Typography variant="body2" sx={{ ml: 2 }}>
                                <strong>Diesel:</strong> {viewingJob.diesel_quantity}L
                              </Typography>
                            )}
                            {viewingJob.def_quantity && (
                              <Typography variant="body2" sx={{ ml: 2 }}>
                                <strong>DEF:</strong> {viewingJob.def_quantity}L
                              </Typography>
                            )}
                          </Box>
                        )}
                        
                        {/* Delivered Quantities - Calculate main job quantity by subtracting sub-job totals */}
                        {(() => {
                          // Calculate sub-job totals
                          let subJobDieselTotal = 0;
                          let subJobDefTotal = 0;
                          if (viewingJob.delivery_completion.sub_jobs && Array.isArray(viewingJob.delivery_completion.sub_jobs)) {
                            viewingJob.delivery_completion.sub_jobs.forEach(subJob => {
                              subJobDieselTotal += parseFloat(subJob.diesel_delivered) || 0;
                              subJobDefTotal += parseFloat(subJob.def_delivered) || 0;
                            });
                          }
                          
                          // Calculate main job quantities by subtracting sub-job totals from delivery_completion totals
                          const mainJobDiesel = Math.max(0, parseFloat(viewingJob.delivery_completion.diesel_delivered || 0) - subJobDieselTotal);
                          const mainJobDef = Math.max(0, parseFloat(viewingJob.delivery_completion.def_delivered || 0) - subJobDefTotal);
                          
                          return (
                            <Box sx={{ mb: 1 }}>
                              <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>Quantities Delivered (Main Job):</Typography>
                              <Typography variant="body2" sx={{ ml: 2 }}>
                                <strong>Diesel:</strong> {mainJobDiesel.toFixed(2)}L
                              </Typography>
                              <Typography variant="body2" sx={{ ml: 2 }}>
                                <strong>DEF:</strong> {mainJobDef.toFixed(2)}L
                              </Typography>
                            </Box>
                          );
                        })()}
                        
                        {/* Job Delivery Notes */}
                        {viewingJob.delivery_notes && (
                          <Box sx={{ mt: 1.5, p: 1, bgcolor: 'background.paper', borderRadius: 1 }}>
                            <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>Job Delivery Notes:</Typography>
                            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                              {viewingJob.delivery_notes}
                            </Typography>
                          </Box>
                        )}
                        
                        {/* Completion Notes */}
                        {viewingJob.delivery_completion.completion_notes && (
                          <Box sx={{ mt: 1.5, p: 1, bgcolor: 'background.paper', borderRadius: 1 }}>
                            <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>Completion Notes:</Typography>
                            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                              {viewingJob.delivery_completion.completion_notes}
                            </Typography>
                          </Box>
                        )}
                        {viewingJob.delivery_completion.completion_latitude && viewingJob.delivery_completion.completion_longitude && (
                          <Typography variant="body2" sx={{ mt: 1, fontSize: '0.75rem', color: 'text.secondary' }}>
                            <strong>Completion Location:</strong> {parseFloat(viewingJob.delivery_completion.completion_latitude).toFixed(6)}, {parseFloat(viewingJob.delivery_completion.completion_longitude).toFixed(6)}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                    
                    {/* Sub Deliveries Section */}
                    {viewingJob.delivery_completion.sub_jobs && Array.isArray(viewingJob.delivery_completion.sub_jobs) && viewingJob.delivery_completion.sub_jobs.length > 0 && (
                      <Box>
                        <Typography variant="h6" gutterBottom sx={{ color: 'info.main', fontWeight: 600 }}>
                          Sub Deliveries ({viewingJob.delivery_completion.sub_jobs.length})
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                          This job was completed with {viewingJob.delivery_completion.sub_jobs.length} additional sub-delivery stop{viewingJob.delivery_completion.sub_jobs.length > 1 ? 's' : ''}:
                        </Typography>
                        {viewingJob.delivery_completion.sub_jobs.map((subJob, index) => {
                          const subJobAddress = [
                            subJob.delivery_address,
                            subJob.delivery_city,
                            subJob.delivery_state,
                            subJob.delivery_zip
                          ].filter(Boolean).join(', ');
                          
                          return (
                            <Box key={subJob.id || index} sx={{ mb: 2, p: 2, bgcolor: 'info.light', borderRadius: 1, border: '1px solid', borderColor: 'info.main' }}>
                              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1.5, color: 'info.dark' }}>
                                Delivery Stop {index + 1}
                              </Typography>
                              
                              <Typography variant="body2" sx={{ mb: 1 }}>
                                <strong>Delivery Address:</strong> {subJobAddress || 'N/A'}
                              </Typography>
                              
                              <Box sx={{ mb: 1 }}>
                                <Typography variant="body2"><strong>Quantities Delivered:</strong></Typography>
                                <Typography variant="body2" sx={{ ml: 2 }}>
                                  <strong>Diesel:</strong> {subJob.diesel_delivered || '0'}L
                                </Typography>
                                <Typography variant="body2" sx={{ ml: 2 }}>
                                  <strong>DEF:</strong> {subJob.def_delivered || '0'}L
                                </Typography>
                              </Box>
                              
                              {subJob.delivery_notes && (
                                <Box sx={{ mt: 1.5, p: 1, bgcolor: 'background.paper', borderRadius: 1 }}>
                                  <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>Delivery Notes:</Typography>
                                  <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                                    {subJob.delivery_notes}
                                  </Typography>
                                </Box>
                              )}
                              
                              {subJob.completion_latitude && subJob.completion_longitude && (
                                <Typography variant="body2" sx={{ mt: 1, fontSize: '0.75rem', color: 'text.secondary' }}>
                                  <strong>Completion Location:</strong> {parseFloat(subJob.completion_latitude).toFixed(6)}, {parseFloat(subJob.completion_longitude).toFixed(6)}
                                </Typography>
                              )}
                              
                              {subJob.completed_at && (
                                <Typography variant="body2" sx={{ mt: 1, fontSize: '0.75rem', color: 'text.secondary' }}>
                                  <strong>Completed At:</strong> {new Date(subJob.completed_at).toLocaleString()}
                                </Typography>
                              )}
                            </Box>
                          );
                        })}
                        
                        {/* Show total delivered quantities - includes main job + all sub-jobs */}
                        {(() => {
                          // Calculate main job quantities (by subtracting sub-jobs from delivery_completion totals)
                          let subJobDieselTotal = 0;
                          let subJobDefTotal = 0;
                          if (viewingJob.delivery_completion.sub_jobs && Array.isArray(viewingJob.delivery_completion.sub_jobs)) {
                            viewingJob.delivery_completion.sub_jobs.forEach(subJob => {
                              subJobDieselTotal += parseFloat(subJob.diesel_delivered) || 0;
                              subJobDefTotal += parseFloat(subJob.def_delivered) || 0;
                            });
                          }
                          
                          const mainJobDiesel = Math.max(0, parseFloat(viewingJob.delivery_completion.diesel_delivered || 0) - subJobDieselTotal);
                          const mainJobDef = Math.max(0, parseFloat(viewingJob.delivery_completion.def_delivered || 0) - subJobDefTotal);
                          
                          // Total = main job + all sub-jobs
                          const totalDiesel = mainJobDiesel + subJobDieselTotal;
                          const totalDef = mainJobDef + subJobDefTotal;
                          
                          return (
                            <Box sx={{ mt: 2, p: 2, bgcolor: 'success.light', borderRadius: 1, border: '1px solid', borderColor: 'success.main' }}>
                              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: 'success.dark' }}>
                                Total Delivered Across All Stops (Main + Sub-Deliveries)
                              </Typography>
                              <Typography variant="body2">
                                <strong>Total Diesel:</strong> {totalDiesel.toFixed(2)}L
                              </Typography>
                              <Typography variant="body2">
                                <strong>Total DEF:</strong> {totalDef.toFixed(2)}L
                              </Typography>
                            </Box>
                          );
                        })()}
                      </Box>
                    )}
                  </Box>
                )}
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => {
              setViewDialogOpen(false);
              setViewingJob(null);
            }}>
              Close
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    </Layout>
  );
};

export default ManagerJobs;



